// Column order and headings for the executive logs, as Luigi's workbook has
// them (Leon, 2026-10-16). Client identity is never a column here: the views
// carry client ID only (0339, 0340).

export type ExecLogName =
  | "bookings"
  | "services"
  | "dispatches"
  | "giftpasses"
  | "giftpass_orders"
  | "client_wallet"
  | "partner_wallet"
  | "rider_sessions"
  | "partner_transactions";

export interface ExecLogColumn {
  key: string;
  label: string;
}

export const EXEC_LOG_COLUMNS: Record<ExecLogName, ExecLogColumn[]> = {
  bookings: [
    { key: "booking_ref", label: "Booking reference" },
    { key: "booking_date", label: "Date" },
    { key: "booking_timestamp", label: "Timestamp" },
    { key: "status", label: "Status" },
    { key: "client_id", label: "Client ID" },
    { key: "subscription", label: "Subscription" },
    { key: "payment_method", label: "Payment method" },
    { key: "service_list_price", label: "Service list price" },
    { key: "surcharge", label: "Surcharge" },
    { key: "giftpass_used", label: "GiftPass used" },
    { key: "wallet_used", label: "Wallet used" },
    { key: "promo_discount", label: "Promo discount" },
    { key: "summary_total", label: "Summary total" },
    { key: "online_processing", label: "Online processing" },
    { key: "cancel_fee", label: "Cancel fee" },
    { key: "amount_paid", label: "Amount paid" },
  ],
  services: [
    { key: "service_ref", label: "Service reference" },
    { key: "booking_ref", label: "Booking reference" },
    { key: "client_id", label: "Client ID" },
    { key: "therapist_name", label: "Therapist name" },
    { key: "mt_code", label: "MT code" },
    { key: "status", label: "Status" },
    { key: "payment_method", label: "Payment method" },
    { key: "service_name", label: "Service type" },
    { key: "service_list_price", label: "Service list price" },
    { key: "therapist_share", label: "Therapist share" },
    { key: "surcharge_share", label: "Surcharge share" },
    { key: "cancellation_share", label: "Cancellation share" },
  ],
  dispatches: [
    { key: "dispatch_ref", label: "Dispatch reference" },
    { key: "dispatch_date", label: "Date" },
    { key: "dispatch_timestamp", label: "Timestamp" },
    { key: "status", label: "Status" },
    { key: "booking_ref", label: "Booking reference" },
    { key: "client_id", label: "Client ID" },
    { key: "rider_name", label: "Rider name" },
    { key: "rider_tier", label: "Tier" },
    { key: "therapist_name", label: "Therapist name" },
    { key: "route_km", label: "Route distance (km)" },
    { key: "total_km", label: "Tracked distance (km)" },
    { key: "route_source", label: "Distance source" },
  ],
  giftpasses: [
    { key: "giftpass_ref", label: "GiftPass reference" },
    { key: "value_php", label: "Value" },
    { key: "status", label: "Status" },
    { key: "claim_state", label: "Claimed or unclaimed" },
    { key: "batch_id", label: "Batch" },
    { key: "campaign", label: "Campaign" },
    { key: "generated_on", label: "Generated on" },
    { key: "redeemed_on", label: "Redeemed on" },
    { key: "redeemed_booking_ref", label: "Redeemed on booking" },
    { key: "redeemed_client_id", label: "Redeemed by (client ID)" },
  ],
  // G11 — Luigi's "GiftPass package logs" sheet, no buyer details (0380).
  giftpass_orders: [
    { key: "order_ref", label: "Order reference" },
    { key: "order_date", label: "Date" },
    { key: "order_timestamp", label: "Timestamp" },
    { key: "status", label: "Status" },
    { key: "package_name", label: "Package" },
    { key: "quantity", label: "Quantity" },
    { key: "total_passes", label: "Passes" },
    { key: "total_amount", label: "Total amount" },
    { key: "processing_fee", label: "Processing fee" },
    { key: "net_amount", label: "Net amount" },
    { key: "seller", label: "Seller (referral)" },
    { key: "source", label: "Source" },
    { key: "payment_method", label: "Payment method" },
    { key: "payment_reference", label: "Reference no." },
    { key: "paid_at", label: "Paid at" },
    { key: "payment_verification", label: "Payment verification" },
    { key: "has_proof", label: "Proof attached" },
    { key: "physical", label: "Physical cards" },
    { key: "delivery_status", label: "Delivery status" },
    { key: "codes_sent", label: "Codes emailed" },
  ],
  client_wallet: [
    { key: "entry_id", label: "Transaction ID" },
    { key: "entry_date", label: "Date" },
    { key: "entry_timestamp", label: "Timestamp" },
    { key: "client_id", label: "Client ID" },
    { key: "entry_type", label: "Type" },
    { key: "amount", label: "Amount" },
    { key: "balance_after", label: "Balance after" },
    { key: "booking_ref", label: "Booking reference" },
    { key: "topup_id", label: "Top-up reference" },
    { key: "note", label: "Note" },
  ],
  partner_wallet: [
    { key: "staff_id", label: "Partner ID" },
    { key: "staff_name", label: "Partner name" },
    { key: "role", label: "Role" },
    { key: "posted_earnings", label: "Posted earnings" },
    { key: "cash_held", label: "Cash held" },
    { key: "payout_held", label: "Payout pending" },
    { key: "cash_retained", label: "Cash retained" },
    { key: "payout_paid", label: "Paid out" },
    { key: "available_balance", label: "Available balance" },
  ],
  partner_transactions: [
    { key: "entry_date", label: "Date" },
    { key: "entry_timestamp", label: "Timestamp" },
    { key: "staff_id", label: "Partner ID" },
    { key: "staff_name", label: "Partner name" },
    { key: "role", label: "Role" },
    { key: "entry_type", label: "Type" },
    { key: "reference", label: "Reference" },
    { key: "amount", label: "Amount" },
  ],
  rider_sessions: [
    { key: "rider_session_id", label: "Session reference" },
    { key: "computed_at", label: "Computed at" },
    { key: "rider_name", label: "Rider name" },
    { key: "rider_tier", label: "Tier" },
    { key: "dispatch_count", label: "Dispatches" },
    { key: "total_km", label: "Distance (km)" },
    { key: "base_php", label: "Base fare" },
    { key: "peak_incentive_php", label: "Peak incentive" },
    { key: "extension_fee_php", label: "Extension fee" },
    { key: "guarantee_php", label: "Guarantee" },
    { key: "cancellation_fees_php", label: "Cancellation fees" },
    { key: "surcharge_shares_php", label: "Surcharge shares" },
    { key: "total_php", label: "Total pay" },
  ],
};

export const EXEC_LOG_TITLES: Record<ExecLogName, string> = {
  bookings: "Booking history",
  services: "Service history",
  dispatches: "Dispatch history",
  giftpasses: "GiftPass log",
  giftpass_orders: "GiftPass orders",
  client_wallet: "Client wallet history",
  partner_wallet: "Partner balances",
  rider_sessions: "Rider session pay",
  partner_transactions: "Partner transactions",
};
